import React, { useState, useMemo, useCallback } from 'react';
import {
  Building2,
  FileText,
  Search,
  X,
  Eye,
  Pencil,
  Loader,
  Phone,
  Mail,
  MapPin,
  Sparkles,
  Trash2,
  ChevronRight,
  Plus,
  Upload,
  UserPlus,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isSalesRole } from '../lib/sales-access';
import { useUrlState } from '../hooks/useUrlState';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import ProvinceSelect from '../components/ProvinceSelect';
import SortTh, { sortCompare, makeToggleSort } from '../components/SortTh';
import { PageHeaderBand } from '../components/layout/PageHeaderBand';
import ScoutButton from '../components/sales/ScoutButton';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import { Tooltip } from '../components/ui/tooltip';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../lib/load-failure';
import { CustomerStatusBadge } from '../components/customers/CustomerStatusBadge';
import { QuickAddProspect } from '../components/customers/QuickAddProspect';
import { useCustomerPermissions } from '../hooks/useCustomerPermissions';
import { CUSTOMER_STATUS_LABELS, customerStatusSkin, type CustomerStatus } from '../lib/customer-status';
import { isCrmRole } from '../lib/roles';
import { formatCalendarDate } from '../lib/calendar-date';
import type { CustomerActivity } from '../types/customer-snapshot';

const PAYMENT_TERMS_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'Net 30', label: 'Net 30' },
  { value: 'Net 60', label: 'Net 60' },
  { value: 'Net 90', label: 'Net 90' },
  { value: 'Due on Receipt', label: 'Due on Receipt' },
];

const BILLING_FREQUENCY_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'per_event', label: 'Per Event' },
  { value: 'per_haul', label: 'Per Haul' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'per_well', label: 'Per Well' },
];

const DAY_OF_WEEK_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '7', label: 'Sunday' },
];

const inputClass =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500/50 transition-colors';

type FilterKey = 'All' | 'Active Jobs' | 'Outstanding' | 'No Balance';
const FILTER_KEYS: readonly FilterKey[] = ['All', 'Active Jobs', 'Outstanding', 'No Balance'];

const activeJobs = (c: any) => c.activeJobCount || c.active_job_count || 0;
/**
 * The balance GET /customers sent, or `null`. Since api#1038 the list returns
 * `outstandingBalance` on every row and sends it as NULL to a role that may not
 * read invoices (sales). A null balance is unknown, not zero: it matches
 * neither Outstanding nor No Balance. (This read `|| 0`, which put every client
 * under "No Balance" for a reader the API had refused the figure.)
 */
const balance = (c: any): number | null => {
  const raw = c.outstandingBalance ?? c.outstanding_balance;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

/**
 * The chips above the list, each carrying the ONE predicate both the list and
 * the chip's count use. The predicates are the screen's existing ones, moved
 * here unchanged.
 *
 * `reads` names the fields a chip's predicate depends on. `GET /customers`
 * returns raw `customers` rows (customers.routes.ts, `db.select().from(customers)`),
 * and that table has no job count and no balance column — so today NO row
 * carries either field. A tally over a field no row carries is not a count,
 * it is `0` by construction, and this page would print "Outstanding 0" as if
 * every client were paid up. When no loaded row carries any of `reads`, the
 * count is absent and renders a dash. If the API ever returns the field, the
 * count appears on its own.
 */
const FILTERS: { key: FilterKey; match: (c: any) => boolean; reads?: string[] }[] = [
  { key: 'All', match: () => true },
  { key: 'Active Jobs', match: (c) => activeJobs(c) > 0, reads: ['activeJobCount', 'active_job_count'] },
  { key: 'Outstanding', match: (c) => (balance(c) ?? 0) > 0, reads: ['outstandingBalance', 'outstanding_balance'] },
  { key: 'No Balance', match: (c) => balance(c) === 0, reads: ['outstandingBalance', 'outstanding_balance'] },
];

/** The two chips that read money. Sales sees AR on the account snapshot only. */
const MONEY_FILTERS: readonly FilterKey[] = ['Outstanding', 'No Balance'];

/**
 * Status chips (CRM-FE-1). The DEFAULT IS ALL, so nothing leaves the office's
 * view: the API's own default list is every status, and an inactive client is
 * still a client someone may need to open.
 */
type StatusKey = 'all' | CustomerStatus;
const STATUS_KEYS: readonly StatusKey[] = ['all', 'prospect', 'active', 'inactive'];
const STATUS_CHIP_LABEL: Record<StatusKey, string> = { all: 'All', ...CUSTOMER_STATUS_LABELS };

/**
 * "Quiet 90+ days" (CRM-FE-4) is the human version of Scout's future flag, and
 * it asks the API the same question Scout will: GET /customers/activity
 * ?quiet_days=90 decides which customers are quiet (last activity before the
 * TENANT's today − 90, or none ever). This screen does no date maths — it
 * shows the customers that answer names. Changing the threshold here changes
 * only this chip; it changes no customer's status.
 */
const QUIET_DAYS = 90;


/** The neutral outlined pill for a free-text value — rate sheet and terms are
 *  not a status, so they take no signal fill. Same pill as Rate Sheets' Type. */
const PILL =
  'inline-flex rounded-fi-pill border border-fieldiq-border px-2.5 py-1 text-[0.68rem] font-bold uppercase tracking-[0.06em] leading-none text-fieldiq-text';

export default function CustomersScreen() {
  const navigate = useNavigate();
  // Sales reads the client list and creates nothing (api#1032): no Import, no New Client.
  const { user } = useAuth();
  const isSales = isSalesRole(user?.role);
  const perms = useCustomerPermissions();
  const crm = isCrmRole(user?.role);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const queryClient = useQueryClient();

  // Search, chip and sort live in the URL (useUrlState), so Back from a client
  // returns to the same results.
  const [search, setSearch] = useUrlState<string>('q', '');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Create form state
  const [formName, setFormName] = useState('');
  const [formContactName, setFormContactName] = useState('');
  const [formContactPhone, setFormContactPhone] = useState('');
  const [formContactEmail, setFormContactEmail] = useState('');
  const [formAddress, setFormAddress] = useState('');
  const [formCity, setFormCity] = useState('');
  const [formProvince, setFormProvince] = useState('');
  const [formPaymentTerms, setFormPaymentTerms] = useState('');
  const [formAccountingEmail, setFormAccountingEmail] = useState('');
  const [formBillingFrequency, setFormBillingFrequency] = useState('');
  const [formBillingAnchor, setFormBillingAnchor] = useState('');
  const [formNotes, setFormNotes] = useState('');

  // AI Lookup state
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [lookupHint, setLookupHint] = useState<{ confidence: string } | null>(null);

  // Query
  // `error` and the paused failure are read too: a list that did not arrive is
  // not "No customers yet" (CLAUDE.md, the three states).
  const customersQuery = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.customers.list().then((r) => {
      const d = r.data;
      return Array.isArray(d) ? d : (d?.data ?? d?.customers ?? []);
    }),
    staleTime: 0,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  // Last activity and the quiet set — CRM_ROLES routes, so only a CRM reader asks.
  const activityQuery = useQuery({
    queryKey: ['customers', 'activity'],
    queryFn: () => api.customers.activity().then((r) => r.data.data as CustomerActivity),
    enabled: crm,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const quietQuery = useQuery({
    queryKey: ['customers', 'activity', { quiet_days: QUIET_DAYS }],
    queryFn: () => api.customers.activity({ quiet_days: QUIET_DAYS }).then((r) => r.data.data as CustomerActivity),
    enabled: crm,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const lastActivity = useMemo(
    () => new Map((activityQuery.data?.rows ?? []).map((r) => [r.customerId, r.lastActivityDate])),
    [activityQuery.data],
  );
  const quietIds = useMemo(
    () => (quietQuery.data ? new Set(quietQuery.data.rows.map((r) => r.customerId)) : null),
    [quietQuery.data],
  );
  const quietFailed = !!queryLoadFailure(quietQuery);
  const [quietParam, setQuietParam] = useUrlState<string>('quiet', '');
  const quietOn = crm && quietParam === '1';
  const lastActivityCell = (id: string) => {
    if (!activityQuery.data) return '—';
    const d = lastActivity.get(id);
    return d ? formatCalendarDate(d) ?? d : '—';
  };

  const loadFailure = queryLoadFailure(customersQuery);
  const customers = loadFailure ? undefined : customersQuery.data;
  // isLoading is FALSE while a failed read waits to retry, so the masks key on
  // "no data yet" instead (memory: is-loading-is-false-while-paused).
  const isLoading = !loadFailure && customersQuery.data === undefined;

  // Delete state
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => api.customers.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      resetForm();
      setShowCreateModal(false);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.customers.delete(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['customers'] });
      const previous = queryClient.getQueryData(['customers']);
      // eslint-disable-next-line no-restricted-syntax -- TODO PR 2: migrate to setQueryDataMapped (see docs/audits/setquerydata-audit.md)
      queryClient.setQueryData(['customers'], (old: any) =>
        old ? (old as any[]).filter((c: any) => c.id !== id) : []
      );
      return { previous };
    },
    onSuccess: () => {
      setDeleteConfirm(null);
    },
    onError: (_err, _id, context) => {
      // eslint-disable-next-line no-restricted-syntax -- TODO PR 2: migrate to setQueryDataMapped (see docs/audits/setquerydata-audit.md)
      if (context?.previous) queryClient.setQueryData(['customers'], context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  function resetForm() {
    setFormName('');
    setFormContactName('');
    setFormContactPhone('');
    setFormContactEmail('');
    setFormAddress('');
    setFormCity('');
    setFormProvince('');
    setFormPaymentTerms('');
    setFormAccountingEmail('');
    setFormBillingFrequency('');
    setFormBillingAnchor('');
    setFormNotes('');
    setLookupLoading(false);
    setLookupError('');
    setLookupHint(null);
  }

  async function handleLookup() {
    if (!formName.trim() || lookupLoading) return;
    setLookupLoading(true);
    setLookupError('');
    setLookupHint(null);
    try {
      const res = await api.customers.lookup(formName.trim());
      const d = res.data?.data;
      if (d) {
        if (d.name) setFormName(d.name);
        if (d.mainContactName) setFormContactName(d.mainContactName);
        if (d.mainContactPhone) setFormContactPhone(d.mainContactPhone);
        if (d.mainContactEmail) setFormContactEmail(d.mainContactEmail);
        if (d.address) setFormAddress(d.address);
        if (d.city) setFormCity(d.city);
        if (d.provinceState) setFormProvince(d.provinceState);
        if (d.paymentTerms) setFormPaymentTerms(d.paymentTerms);
        if (d.accountingEmail) setFormAccountingEmail(d.accountingEmail);
        if (d.notes) setFormNotes(d.notes);
        setLookupHint({ confidence: d.confidence || 'low' });
      }
    } catch (err: any) {
      setLookupError(err.response?.data?.error || 'AI lookup failed');
    } finally {
      setLookupLoading(false);
    }
  }

  function handleCreate() {
    if (!formName.trim()) return;
    createMutation.mutate({
      name: formName.trim(),
      mainContactName: formContactName.trim() || null,
      mainContactPhone: formContactPhone.trim() || null,
      mainContactEmail: formContactEmail.trim() || null,
      accountingEmail: formAccountingEmail.trim() || null,
      address: formAddress.trim() || null,
      city: formCity.trim() || null,
      provinceState: formProvince.trim() || null,
      paymentTerms: formPaymentTerms || null,
      defaultBillingFrequency: formBillingFrequency || null,
      // `billingAnchorDay`: the same one name the detail page's edit modal and
      // GET /customers use. This sent `billingAnchor`, which POST /customers
      // never read, so an anchor typed at create was dropped at the door.
      billingAnchorDay: formBillingAnchor ? Number(formBillingAnchor) : null,
      notes: formNotes.trim() || null,
    });
  }

  const visibleFilters = isSales ? FILTERS.filter((f) => !MONEY_FILTERS.includes(f.key)) : FILTERS;
  const [filterParam, setFilterTab] = useUrlState<FilterKey>('filter', 'All');
  // A hand-edited or stale link can carry anything; an unknown chip is All.
  const filterTab: FilterKey =
    FILTER_KEYS.includes(filterParam) && visibleFilters.some((f) => f.key === filterParam) ? filterParam : 'All';
  const [statusParam, setStatusTab] = useUrlState<string>('status', 'all');
  const statusTab: StatusKey = (STATUS_KEYS as readonly string[]).includes(statusParam) ? (statusParam as StatusKey) : 'all';
  const [sortBy, setSortBy] = useUrlState<string>('sort', 'name');
  const [sortDir, setSortDir] = useUrlState<'asc' | 'desc'>('dir', 'asc');

  // Filtered customers
  const allCustomers = useMemo(() => {
    if (!customers || !Array.isArray(customers)) return [];
    let list = customers as any[];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (c: any) =>
          (c.name || '').toLowerCase().includes(q) ||
          (c.mainContactName || c.main_contact_name || '').toLowerCase().includes(q) ||
          (c.city || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [customers, search]);

  // Status chip, then tab filter — the same predicates the chip counts use
  const filtered = useMemo(() => {
    const byStatus = statusTab === 'all' ? allCustomers : allCustomers.filter((c: any) => c.status === statusTab);
    // The quiet set is the API's answer; while it is not in, nothing is shown
    // rather than everything.
    const byQuiet = quietOn ? byStatus.filter((c: any) => quietIds?.has(c.id)) : byStatus;
    const tab = FILTERS.find((f) => f.key === filterTab);
    return tab ? byQuiet.filter(tab.match) : byQuiet;
  }, [allCustomers, filterTab, statusTab, quietOn, quietIds]);

  /**
   * Per-chip tallies over the whole list, and the band's figures beside them.
   * `null` until the list has arrived, or if it failed.
   *
   * A TALLY OF THE LOADED ROWS IS THE HONEST SOURCE HERE, for the Rate Sheets
   * reason: `GET /customers` returns every live customer for the company with
   * no LIMIT and no pagination, and no endpoint returns these figures —
   * verified in customers.routes.ts. Like Rate Sheets, the counts are
   * company-wide and do not follow the search box.
   *
   * A chip whose field no row carries counts as ABSENT, not zero. See FILTERS.
   */
  const counts = useMemo(() => {
    if (!customers || !Array.isArray(customers)) return null;
    const all = customers as any[];
    const chip = Object.fromEntries(
      FILTERS.map((f) => [
        f.key,
        f.reads && !all.some((c) => f.reads!.some((k) => c[k] !== null && c[k] !== undefined))
          ? undefined
          : all.filter(f.match).length,
      ]),
    ) as Record<FilterKey, number | undefined>;
    /*
     * STATUS COUNTS ARE A TALLY OF THE LOADED ROWS, and that is honest here
     * for the reason above: GET /customers is unpaginated and returns every
     * live customer in every status by default, and no endpoint returns
     * per-status counts (customers.routes.ts at api f578fa2). A row whose
     * status is outside the enum is counted under All only.
     */
    const status = Object.fromEntries(
      STATUS_KEYS.map((k) => [k, k === 'all' ? all.length : all.filter((c) => c.status === k).length]),
    ) as Record<StatusKey, number>;
    return {
      chip,
      status,
      terms: all.filter((c) => c.paymentTerms || c.payment_terms).length,
      arEmail: all.filter((c) => c.accountingEmail || c.accounting_email).length,
      contact: all.filter((c) => c.mainContactName || c.main_contact_name).length,
    };
  }, [customers]);

  const toggleSort = makeToggleSort(setSortBy, setSortDir, sortBy);
  const sortedCustomers = useMemo(() => {
    return [...filtered].sort((a: any, b: any) => {
      let aVal: any, bVal: any;
      switch (sortBy) {
        case 'name': aVal = a.name || ''; bVal = b.name || ''; break;
        case 'location': aVal = a.city || ''; bVal = b.city || ''; break;
        case 'contact': aVal = a.mainContactName || a.main_contact_name || ''; bVal = b.mainContactName || b.main_contact_name || ''; break;
        case 'rateSheet': aVal = a.rateSheetName || a.rate_sheet_name || 'Book Rate'; bVal = b.rateSheetName || b.rate_sheet_name || 'Book Rate'; break;
        case 'terms': aVal = a.paymentTerms || a.payment_terms || ''; bVal = b.paymentTerms || b.payment_terms || ''; break;
        case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
        case 'owner': aVal = a.ownerName || ''; bVal = b.ownerName || ''; break;
        default: return 0;
      }
      return sortCompare(aVal, bVal, sortDir);
    });
  }, [filtered, sortBy, sortDir]);

  const openCustomer = (id: string) => navigate(`/customers/${id}`);

  /**
   * Enter and Space open a row — but only when the ROW has focus.
   *
   * These rows hold a phone link, the same shape as Rate Sheets' View and Edit
   * buttons (fe#647): a key on the link bubbles here, and handling it would
   * `preventDefault` the link's own activation. Hence the target check, and
   * hence no `role="button"` on the row — an explicit button role makes its
   * children presentational, and the link would stop being announced.
   */
  const rowKeys = (id: string) => (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openCustomer(id);
    }
  };

  return (
    <div data-testid="clients-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Customer accounts"
        title="Clients"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          isSales ? (
            <>
              {/* A rep has no Overview: Scout lives on the screens they open.
                  New prospect becomes the band's secondary beside it. */}
              <ScoutButton />
              {perms.canCreateProspect && (
                <button
                  type="button"
                  onClick={() => setShowQuickAdd(true)}
                  className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
                >
                  <UserPlus className="size-4" aria-hidden="true" />
                  New prospect
                </button>
              )}
            </>
          ) : <>
            {/* No colour class on these: they inherit the band's near-white,
                which theme.css holds up on the navy fill in light mode. */}
            {perms.canCreateProspect && (
              <button
                type="button"
                onClick={() => setShowQuickAdd(true)}
                className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
              >
                <UserPlus className="size-4" aria-hidden="true" />
                New prospect
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/import/clients')}
              className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
            >
              <Upload className="size-4" aria-hidden="true" />
              Import
            </button>
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="fi-action fi-action--standard fi-action--primary"
            >
              <Plus className="size-4" aria-hidden="true" />
              New Client
            </button>
          </>
        }
        /* Tallies of the list this screen already loads — no added query. Absent
           while the list is in flight or failed, and displayValue() renders that
           as a gap rather than a zero. A real zero stays a zero. */
        stats={[
          {
            icon: <Building2 className="size-4" aria-hidden="true" />,
            label: 'Clients',
            sub: 'On file',
            tone: 'neutral',
            loading: isLoading,
            value: displayValue(counts?.chip.All),
          },
          {
            icon: <FileText className="size-4" aria-hidden="true" />,
            label: 'Terms Set',
            sub: 'Payment terms',
            tone: 'info',
            loading: isLoading,
            value: displayValue(counts?.terms),
          },
          {
            icon: <Mail className="size-4" aria-hidden="true" />,
            label: 'AR Email',
            sub: 'Invoice routing',
            tone: 'success',
            loading: isLoading,
            value: displayValue(counts?.arEmail),
          },
          {
            icon: <Phone className="size-4" aria-hidden="true" />,
            label: 'Contact',
            sub: 'Main contact named',
            tone: 'accent',
            loading: isLoading,
            value: displayValue(counts?.contact),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto">
              <div role="group" aria-label="Status" className="flex flex-shrink-0 gap-2">
                {STATUS_KEYS.map((k) => {
                  const active = statusTab === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setStatusTab(k)}
                      className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                        active
                          ? 'bg-fi-amber text-fi-amber-ink'
                          : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                      }`}
                    >
                      {STATUS_CHIP_LABEL[k]}
                      {!isLoading && (
                        <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>
                          {displayValue(counts?.status[k])}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <span aria-hidden="true" className="mx-1 w-px flex-shrink-0 self-stretch bg-fieldiq-border" />
              {visibleFilters.map((f) => {
                const active = filterTab === f.key;
                return (
                  <button
                    key={f.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setFilterTab(f.key)}
                    className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                      active
                        ? 'bg-fi-amber text-fi-amber-ink'
                        : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                    }`}
                  >
                    {f.key === 'All' ? 'Any' : f.key}
                    {/* No count is claimed while the list is in flight. Full
                        ink on the active chip: the reference pages' 70% ink on
                        amber measures 3.7:1, under the 4.5:1 floor. */}
                    {!isLoading && (
                      <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>
                        {displayValue(counts?.chip[f.key])}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {crm && (
              <Tooltip
                content={quietFailed ? "Couldn't load which customers are quiet" : `No job, ticket, invoice or meeting in ${QUIET_DAYS} days`}
                className="flex-shrink-0"
              >
              <button
                type="button"
                aria-pressed={quietOn}
                onClick={() => setQuietParam(quietOn ? '' : '1')}
                className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                  quietOn ? 'bg-fi-amber text-fi-amber-ink' : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                }`}
              >
                Quiet {QUIET_DAYS}+ days
                {quietQuery.data && (
                  <span className={`tabular-nums ${quietOn ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>{quietQuery.data.rows.length}</span>
                )}
                {quietFailed && <span className={quietOn ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}>—</span>}
              </button>
              </Tooltip>
            )}

            <div className="relative lg:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fieldiq-muted" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, contact, or city..."
                aria-label="Search clients"
                className="w-full bg-fieldiq-card border border-fieldiq-border rounded-fi-control pl-10 pr-8 py-1.5 text-sm text-white placeholder:text-fieldiq-muted focus:outline-none focus:border-fi-amber"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-fieldiq-muted hover:text-white"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* The register. The amber edge is on this container; each row
              carries a status rail (CRM-FE-1), transparent for a status the
              map does not know. */}
          <section
            aria-labelledby="client-register-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="flex items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
              <div>
                <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Client register</p>
                <h2 id="client-register-title" className="mt-1 text-base font-extrabold tracking-tight text-white">Accounts and contacts</h2>
              </div>
              {!isLoading && customers && (
                <p className="text-sm text-fieldiq-muted tabular-nums">
                  {filtered.length} client{filtered.length !== 1 ? 's' : ''}
                </p>
              )}
            </header>

            {loadFailure && (
              <div className="p-4">
                <LoadFailureNotice error={queryFailureError(customersQuery)} subject="clients" />
              </div>
            )}

            {/* Loading skeleton */}
            {isLoading && (
              <div className="space-y-3 p-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="rounded-fi-panel bg-fieldiq-dark/60 p-4 animate-pulse">
                    <div className="h-4 w-1/3 rounded bg-fieldiq-border" />
                    <div className="mt-2 h-3 w-1/4 rounded bg-fieldiq-border" />
                  </div>
                ))}
              </div>
            )}

            {/* Empty state */}
            {!isLoading && customers && (customers as any[]).length === 0 && (
              <div className="px-4 py-16 text-center">
                <Building2 size={40} className="mx-auto mb-3 text-fieldiq-muted" />
                <p className="text-white text-lg mb-2">No customers yet</p>
                <p className="text-fieldiq-muted text-sm">Add your first customer to get started.</p>
              </div>
            )}

            {/* No results */}
            {!isLoading && customers && (customers as any[]).length > 0 && filtered.length === 0 && (
              <div className="px-4 py-16 text-center text-fieldiq-muted text-sm">No customers found</div>
            )}

            {/* Customer list */}
            {!isLoading && filtered.length > 0 && (
              <>
                {/* ── Desktop Table ── */}
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[1100px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                      <tr className="border-l-4 border-l-transparent">
                        <SortTh label="Customer" field="name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortTh label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortTh label="Location" field="location" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortTh label="Contact" field="contact" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortTh label="Rate Sheet" field="rateSheet" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortTh label="Terms" field="terms" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        <SortTh label="Owner" field="owner" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                        {crm && <th className="whitespace-nowrap px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wider text-fieldiq-muted">Last activity</th>}
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCustomers.map((customer: any) => {
                        const contactName = customer.mainContactName || customer.main_contact_name;
                        const contactPhone = customer.mainContactPhone || customer.main_contact_phone;
                        const city = customer.city;
                        const province = customer.provinceState || customer.province_state;
                        const paymentTerms = customer.paymentTerms || customer.payment_terms;

                        return (
                          <tr
                            key={customer.id}
                            tabIndex={0}
                            onClick={() => openCustomer(customer.id)}
                            onKeyDown={rowKeys(customer.id)}
                            className={`cursor-pointer border-l-4 border-t border-fieldiq-border outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${customerStatusSkin(customer.status).rail}`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-fi-control bg-fieldiq-accent/15 flex items-center justify-center shrink-0">
                                  <span className="text-xs font-bold text-fieldiq-accent">{(customer.name || '?').charAt(0)}</span>
                                </div>
                                <span className="text-sm font-extrabold text-white">{customer.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <CustomerStatusBadge status={customer.status} />
                            </td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">
                              {[city, province].filter(Boolean).join(', ') || '—'}
                            </td>
                            <td className="px-4 py-3">
                              <div>
                                <p className="text-sm font-bold text-fieldiq-text">{contactName || '—'}</p>
                                {contactPhone && (
                                  <a href={`tel:${contactPhone}`} onClick={(e) => e.stopPropagation()} className="text-sm text-fieldiq-accent hover:underline flex items-center gap-0.5 mt-0.5">
                                    <Phone className="w-2.5 h-2.5" />{contactPhone}
                                  </a>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={PILL}>
                                {customer.rateSheetName || customer.rate_sheet_name || 'Book Rate'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {paymentTerms ? (
                                <span className={PILL}>{paymentTerms}</span>
                              ) : (
                                <span className="text-sm text-fieldiq-muted">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">
                              {customer.ownerName || '—'}
                            </td>
                            {crm && (
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{lastActivityCell(customer.id)}</td>
                            )}
                            <td className="pr-3 py-3">
                              <ChevronRight size={14} className="text-fieldiq-muted" />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ── Mobile Card List ── no amber rail here; that edge belongs
                    to the surrounding register, never to a row. */}
                <div className="md:hidden space-y-3 p-4">
                  {sortedCustomers.map((customer: any) => {
                    const contactName = customer.mainContactName || customer.main_contact_name;
                    const contactPhone = customer.mainContactPhone || customer.main_contact_phone;
                    const contactEmail = customer.mainContactEmail || customer.main_contact_email;
                    const city = customer.city;
                    const province = customer.provinceState || customer.province_state;
                    const paymentTerms = customer.paymentTerms || customer.payment_terms;

                    return (
                      <div
                        key={customer.id}
                        onClick={() => openCustomer(customer.id)}
                        className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4 cursor-pointer hover:border-fieldiq-muted transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 rounded-fi-control bg-fieldiq-accent/15 flex items-center justify-center shrink-0">
                              <span className="text-xs font-bold text-fieldiq-accent">{(customer.name || '?').charAt(0)}</span>
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-sm font-extrabold text-white truncate">{customer.name}</h3>
                              <p className="text-sm text-fieldiq-muted">{[city, province].filter(Boolean).join(', ') || '—'}</p>
                            </div>
                          </div>
                          <div className="flex flex-shrink-0 flex-col items-end gap-1">
                            <CustomerStatusBadge status={customer.status} />
                            {paymentTerms && <span className={PILL}>{paymentTerms}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-fieldiq-muted mt-2">
                          {contactName && <span>{contactName}</span>}
                          {contactName && <span>·</span>}
                          <span>{customer.rateSheetName || customer.rate_sheet_name || 'Book Rate'}</span>
                        </div>
                        <p className="mt-1 text-sm text-fieldiq-muted">Owner: {customer.ownerName || '—'}</p>
                        {crm && <p className="text-sm text-fieldiq-muted">Last activity: {lastActivityCell(customer.id)}</p>}
                        {(contactPhone || contactEmail) && (
                          <div className="flex gap-3 mt-2">
                            {contactPhone && (
                              <a href={`tel:${contactPhone}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 text-sm text-fieldiq-accent font-medium">
                                <Phone className="w-3 h-3" /> Call
                              </a>
                            )}
                            {contactEmail && (
                              <a href={`mailto:${contactEmail}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 text-sm text-fieldiq-accent font-medium">
                                <Mail className="w-3 h-3" /> Email
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </section>
        </div>
      </section>

      {/* Create Customer Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-fieldiq-card rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-fieldiq-border">
              <h3 className="text-lg font-bold text-white">New Customer</h3>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-white hover:text-white">
                <X size={20} />
              </button>
            </div>

            {/* Modal body */}
            <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
              {createMutation.isError && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-3 text-sm">
                  {(createMutation.error as any)?.response?.data?.error || 'Failed to create customer'}
                </div>
              )}

              <div>
                <label className="text-sm text-white block mb-1">Company Name *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formName}
                    onChange={(e) => { setFormName(e.target.value); setLookupHint(null); setLookupError(''); }}
                    placeholder="Enter company name"
                    className={`${inputClass} flex-1`}
                    autoFocus
                  />
                  <Tooltip content="AI Lookup" className="flex-shrink-0">
                    <button
                      type="button"
                      onClick={handleLookup}
                      disabled={!formName.trim() || lookupLoading}
                      className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                    >
                      {lookupLoading ? <Loader size={14} className="animate-spin" /> : <Sparkles size={14} />}
                      Lookup
                    </button>
                  </Tooltip>
                </div>
                {lookupError && (
                  <p className="text-xs text-red-400 mt-1">{lookupError}</p>
                )}
                {lookupHint && (
                  <p className="text-xs text-purple-400 mt-1">
                    AI-suggested ({lookupHint.confidence} confidence) — please verify
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-white block mb-1">Main Contact Name</label>
                  <input
                    type="text"
                    value={formContactName}
                    onChange={(e) => setFormContactName(e.target.value)}
                    placeholder="Contact person"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-sm text-white block mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formContactPhone}
                    onChange={(e) => setFormContactPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-white block mb-1">Email</label>
                <input
                  type="email"
                  value={formContactEmail}
                  onChange={(e) => setFormContactEmail(e.target.value)}
                  placeholder="contact@company.com"
                  className={inputClass}
                />
              </div>

              <div>
                <label className="text-sm text-white block mb-1">Accounting / AR Email</label>
                <input
                  type="email"
                  value={formAccountingEmail}
                  onChange={(e) => setFormAccountingEmail(e.target.value)}
                  placeholder="ar@company.com"
                  className={inputClass}
                />
              </div>

              <div>
                <label className="text-sm text-white block mb-1">Address</label>
                <input
                  type="text"
                  value={formAddress}
                  onChange={(e) => setFormAddress(e.target.value)}
                  placeholder="Street address"
                  className={inputClass}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-white block mb-1">City</label>
                  <input
                    type="text"
                    value={formCity}
                    onChange={(e) => setFormCity(e.target.value)}
                    placeholder="City"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-sm text-white block mb-1">Province / State</label>
                  <ProvinceSelect value={formProvince} onChange={setFormProvince} className={inputClass} />
                </div>
              </div>

              <div>
                <label className="text-sm text-white block mb-1">Payment Terms</label>
                <select
                  value={formPaymentTerms}
                  onChange={(e) => setFormPaymentTerms(e.target.value)}
                  className={inputClass}
                >
                  {PAYMENT_TERMS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Billing Section */}
              <div className="border-t border-fieldiq-border pt-4 mt-4">
                <h4 className="text-xs font-bold text-white tracking-wider uppercase mb-3">Billing</h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-white block mb-1">Default Billing Frequency *</label>
                    <select
                      value={formBillingFrequency}
                      onChange={(e) => { setFormBillingFrequency(e.target.value); setFormBillingAnchor(''); }}
                      className={inputClass}
                    >
                      {BILLING_FREQUENCY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  {(formBillingFrequency === 'weekly' || formBillingFrequency === 'biweekly') && (
                    <div>
                      <label className="text-sm text-white block mb-1">Billing Anchor (Day of Week)</label>
                      <select
                        value={formBillingAnchor}
                        onChange={(e) => setFormBillingAnchor(e.target.value)}
                        className={inputClass}
                      >
                        <option value="">Select day...</option>
                        {DAY_OF_WEEK_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <p className="text-xs text-fieldiq-muted mt-1">Invoices will be generated on this day of the week</p>
                    </div>
                  )}

                  {formBillingFrequency === 'monthly' && (
                    <div>
                      <label className="text-sm text-white block mb-1">Billing Anchor (Day of Month)</label>
                      <input
                        type="number"
                        min={1}
                        max={31}
                        value={formBillingAnchor}
                        onChange={(e) => setFormBillingAnchor(e.target.value)}
                        placeholder="1-31"
                        className={inputClass}
                      />
                      <p className="text-xs text-fieldiq-muted mt-1">Invoices will be generated on this day of the month</p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm text-white block mb-1">Notes</label>
                <textarea
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  rows={3}
                  placeholder="Any additional notes..."
                  className={inputClass}
                />
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-fieldiq-border">
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className="px-4 py-2 text-sm text-white hover:text-white transition-colors"
                disabled={createMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending || !formName.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-fieldiq-accent text-black font-semibold rounded-lg text-sm hover:bg-fieldiq-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createMutation.isPending && <Loader size={14} className="animate-spin" />}
                {createMutation.isPending ? 'Creating...' : 'Create Customer'}
              </button>
            </div>
          </div>
        </div>
      )}

      <QuickAddProspect
        open={showQuickAdd}
        onClose={() => setShowQuickAdd(false)}
        onCreated={(c) => { setShowQuickAdd(false); navigate(`/customers/${c.id}`); }}
        onUseExisting={(id) => { setShowQuickAdd(false); navigate(`/customers/${id}`); }}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-fieldiq-card rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-bold text-white mb-2">Delete Customer</h3>
            <p className="text-sm text-fieldiq-muted mb-1">
              Are you sure you want to delete
            </p>
            <p className="text-sm font-semibold text-white mb-4">
              {deleteConfirm.name}?
            </p>
            {deleteMutation.isError && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-3 text-sm mb-4">
                {(deleteMutation.error as any)?.response?.data?.error || 'Failed to delete customer'}
              </div>
            )}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => { setDeleteConfirm(null); deleteMutation.reset(); }}
                className="px-4 py-2 text-sm text-white hover:text-white transition-colors"
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm.id)}
                disabled={deleteMutation.isPending}
                className="flex items-center gap-2 px-5 py-2 bg-red-600 text-white font-semibold rounded-lg text-sm hover:bg-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteMutation.isPending && <Loader size={14} className="animate-spin" />}
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
